import React from 'react';
import { X, Droplets } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';

export interface BrackishZoneProperties {
  id: string | number;
  name: string | null;
  zone_type: string;
  aquifer_name: string | null;
  tds_min: number | null;
  tds_max: number | null;
  area_sq_miles: number | null;
  source: string | null;
}

interface Props {
  zone: BrackishZoneProperties;
  onClose: () => void;
}

const BRACKISH_DESCRIPTION =
  'Mapped lateral extent of brackish groundwater (TDS ~1,000–10,000 mg/L) per TWDB BRACS characterization. ' +
  'Inside this polygon, drillers have encountered brackish water at depth — a usable supply for BTM cooling ' +
  'with standard treatment, and typically outside the "fresh" water rights competition.';

export const BrackishZoneDetail: React.FC<Props> = ({ zone, onClose }) => {
  const { theme } = useTheme();
  const color = '#0f766e';
  const aquifer = zone.aquifer_name || zone.name || 'Unnamed aquifer';

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Droplets size={18} color={color} />
          <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>
            Brackish Aquifer Zone
          </h2>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
          color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {/* Aquifer badge */}
        <div style={{
          padding: '10px 14px', marginBottom: '16px', borderRadius: '8px',
          background: color + '15', border: `1px solid ${color}30`,
        }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color, marginBottom: '6px' }}>
            {aquifer}
          </div>
          <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.5 }}>
            {BRACKISH_DESCRIPTION}
          </div>
        </div>

        {/* Zone facts */}
        <Section title="Zone Details">
          <Field label="Aquifer" value={aquifer} />
          <Field label="Zone Type" value={zone.zone_type === 'production_zone' ? 'HB 30 Production Zone' : 'Brackish Extent'} />
          {(zone.tds_min != null || zone.tds_max != null) && (
            <Field
              label="TDS Range"
              value={`${zone.tds_min?.toLocaleString() ?? '?'}–${zone.tds_max?.toLocaleString() ?? '?'} mg/L`}
            />
          )}
          {zone.area_sq_miles != null && (
            <Field label="Area" value={`${Math.round(Number(zone.area_sq_miles)).toLocaleString()} sq mi`} />
          )}
        </Section>

        {/* BTM sourcing guidance */}
        <Section title="BTM Water Sourcing">
          <div style={{
            padding: '10px', borderRadius: '6px', marginBottom: '8px',
            background: 'rgba(20,184,166,0.1)', border: '1px solid rgba(20,184,166,0.25)',
          }}>
            <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '6px', color: '#0f766e' }}>
              Usable with standard treatment
            </div>
            <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.6 }}>
              For a ~1 AFY (≈893 gpd) BTM cooling load, a single brackish well in this zone is typically
              a viable primary supply. TDS in the 1,000–10,000 mg/L range is single-pass RO territory:
              ~$150–400K installed for a small skid, 60–80% recovery. Brine concentrate can often be trucked
              to a nearby Class II SWD.
            </div>
          </div>
          <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.6 }}>
            Toggle <strong>Water Quality (TDS)</strong> in the sidebar to see actual TDS values from wells
            within and near this polygon before committing to a supply strategy.
          </div>
        </Section>

        {/* Permitting caveat */}
        <Section title="Permitting Notes">
          <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.6 }}>
            HB 30 (2015) relaxed permitting requirements for brackish groundwater production in specific
            designated zones. Verify the site's GCD (Groundwater Conservation District) rules — many GCDs
            have higher exempt-use thresholds (often 10k–25k gpd) below which no permit is required.
          </div>
        </Section>

        {/* Source */}
        <Section title="Data Source">
          <div style={{ fontSize: '11px', color: theme.textTertiary, lineHeight: 1.6 }}>
            TWDB BRACS (Brackish Resources Aquifer Characterization System), 2024 update.
          </div>
        </Section>
      </div>
    </div>
  );
};
